import React from 'react';
import { Clock, Calendar, ChevronRight, ChevronDown } from 'lucide-react';
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status: 'PAID' | 'UNPAID';
  completed_at?: string;
}

interface PayrollResourceSummaryProps {
  resourceId: string;
  data: {
    resource: {
      first_name: string;
      last_name: string;
      email: string;
      compensation_type: string;
      flat_rate_amount: number | null;
      hourly_rate: number | null;
      avatar_url?: string;
    };
    timeclock: {
      entries: any[];
      totalHours: number;
      totalEarnings: number;
    };
    bookings: Record<string, {
      booking_number: number;
      booking_title: string;
      start_date: string;
      end_date: string;
      items: any[];
      totalHours: number;
      totalEarnings: number;
    }>;
    totalHours: number;
    totalEarnings: number;
  };
  isExpanded: boolean;
  onToggle: () => void;
  onToggleBooking: (bookingId: string) => void;
  expandedBookings: Set<string>;
}

// Helper function to format duration
const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

// Helper function to safely format dates
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (!isValid(date)) return '-';
  return format(date, 'MMM d, yyyy');
};

// Helper function to safely format times
const formatTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (!isValid(date)) return '-';
  return format(date, 'h:mm a');
};

// Helper function to safely calculate elapsed time
const getElapsedTime = (startStr: string | null | undefined): string => {
  if (!startStr) return '-';
  const date = new Date(startStr);
  if (!isValid(date)) return '-';
  return formatDistanceToNow(date);
};

export default function PayrollResourceSummary({
  resourceId,
  data,
  isExpanded,
  onToggle,
  onToggleBooking,
  expandedBookings
}: PayrollResourceSummaryProps) {
  return (
    <div className="border-b border-gray-200">
      <div 
        className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center space-x-4">
          <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white overflow-hidden">
            {data.resource.avatar_url ? (
              <img 
                src={data.resource.avatar_url} 
                alt={`${data.resource.first_name} ${data.resource.last_name}`}
                className="h-full w-full object-cover"
              />
            ) : (
              `${data.resource.first_name[0]}${data.resource.last_name[0]}`
            )}
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-900">
              {data.resource.first_name} {data.resource.last_name}
            </h3>
            <p className="text-sm text-gray-500">{data.resource.email}</p>
            <div className="mt-2 flex items-center space-x-4">
              <div className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">
                  {formatDuration(data.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                </span> Total
              </div>
              <div className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">${data.timeclock.totalEarnings.toFixed(2)}</span> Timeclock
              </div>
              <div className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">${Object.values(data.bookings).reduce((sum, booking) => 
                  sum + booking.items.reduce((itemSum, item) => 
                    itemSum + (item.entry_type === 'flat_rate' ? item.earnings : 0), 
                    0
                  ), 0).toFixed(2)}</span> Flat Rate
              </div>
              <div className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">${Object.values(data.bookings).reduce((sum, booking) => 
                  sum + booking.items.reduce((itemSum, item) => 
                    itemSum + (item.entry_type === 'commission' ? item.earnings : 0), 
                    0
                  ), 0).toFixed(2)}</span> Commission
              </div>
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-medium text-gray-900 mb-1">
            ${data.totalEarnings.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 flex items-center">
            <span className="mr-1">Total Earnings</span>
            <ChevronDown className={`inline-block h-4 w-4 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`} />
          </p>
        </div>
      </div>

      {isExpanded && (
      <div className="bg-gray-50 border-t border-gray-200">
        {/* Timeclock Entries */}
        {data.timeclock.entries.length > 0 && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Timeclock Entries</h3>
                <p className="text-xs text-gray-500 mt-1">
                  ${data.timeclock.totalEarnings.toFixed(2)}
                </p>
              </div>
              <div className="text-sm text-gray-500">
                {formatDuration(data.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
              </div>
            </div>
            <div className="space-y-3">
              {data.timeclock.entries.map((entry) => (
                <div key={entry.id} className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {format(new Date(entry.clock_in), 'MMM d, yyyy')}
                      </div>
                      <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                        <span>
                        {format(new Date(entry.clock_in), 'h:mm a')} - {
                          entry.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : 'In Progress'
                        }
                        </span>
                        <span className="font-medium text-gray-900">
                          ${((entry.total_minutes || 0) / 60 * (data.resource.hourly_rate || 0)).toFixed(2)}
                        </span>
                      </div>
                      {entry.break_start && (
                        <div className="mt-1 text-xs text-gray-500">
                          Break: {format(new Date(entry.break_start), 'h:mm a')} - {
                            entry.break_end ? format(new Date(entry.break_end), 'h:mm a') : 'In Progress'
                          }
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {formatDuration(entry.total_minutes || 0)}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center justify-between">
                        <span>
                        {entry.total_minutes ? (
                          `${Math.floor(entry.total_minutes / 60)}h ${entry.total_minutes % 60}m`
                        ) : (
                          getElapsedTime(entry.clock_in)
                        )}
                        </span>
                        <span className="font-medium text-gray-900">
                          ${((entry.total_minutes || 0) / 60 * (data.resource.hourly_rate || 0)).toFixed(2)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <span className={`px-2 py-0.5 rounded-full ${
                          entry.process_status === 'PAID' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {entry.process_status === 'PAID' ? 'PAID' : 'UNPAID'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payroll Rate Items */}
        {Object.entries(data.bookings).map(([bookingId, booking]) => (
          <div key={bookingId}>
            <div 
              className="px-6 py-4 flex items-center justify-between bg-gray-50 border-t border-b border-gray-200 cursor-pointer hover:bg-gray-100"
              onClick={() => onToggleBooking(`${resourceId}-${bookingId}`)}
            >
              <div>
                <div className="flex-1 min-w-0">
                  <span>Job #{booking.booking_number} - {booking.booking_title}</span>
                  {booking.end_date && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {format(parseISO(booking.end_date), 'MMM d, yyyy')}
                    </div>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Total: {booking.totalHours.toFixed(1)}h • ${booking.totalEarnings.toFixed(2)}
                </div>
              </div>
              <ChevronDown 
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                  expandedBookings.has(`${resourceId}-${bookingId}`) ? 'rotate-180' : ''
                }`}
              />
            </div>
            
            {expandedBookings.has(`${resourceId}-${bookingId}`) && (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {booking.items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.item_name}</div>
                        {item.item_description && (
                          <div className="text-sm text-gray-500">{item.item_description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.completed_at || item.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.hours.toFixed(1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.entry_type === 'commission' 
                          ? 'Commission'
                          : `$${item.compensation_type === 'flat_rate' 
                            ? item.flat_rate_amount?.toFixed(2)
                            : item.hourly_rate?.toFixed(2)
                          }/hr`
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="text-sm font-medium text-gray-900">
                          ${item.earnings.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          <span className={`px-2 py-0.5 rounded-full ${
                            item.process_status === 'PAID' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.process_status === 'PAID' ? 'PAID' : 'UNPAID'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>)}
    </div>
  );
}